import type { FC } from 'react';
import React, { useState, useCallback, useEffect, useMemo, useRef, useContext } from 'react';
import { defineMessages, FormattedMessage } from 'react-intl';
import { css } from '@compiled/react';

import Tooltip from '@atlaskit/tooltip/Tooltip';
import Lozenge from '@atlaskit/lozenge/lozenge';
// eslint-disable-next-line @atlaskit/design-system/no-emotion-primitives -- to be migrated to @atlaskit/primitives/compiled – go/akcss
import Box from '@atlaskit/primitives/box';
import { xcss } from '@atlaskit/primitives/xcss';
import { Grid } from '@atlaskit/primitives/grid';
import Button from '@atlaskit/button/default/button';
import AKSpinner from '@atlaskit/spinner/spinner';
import type { CreateUIAnalyticsEvent } from '@atlaskit/analytics-next/types';
import { useAnalyticsEvents } from '@atlaskit/analytics-next';
import { token } from '@atlaskit/tokens';
import { LinkMenuItem } from '@atlaskit/side-nav-items/link-menu-item';
import {
	ExpandableMenuItem,
	ExpandableMenuItemTrigger,
} from '@atlaskit/side-nav-items/expandable-menu-item';

import { NetworkStatus } from '@confluence/apollo-relay-interop/entry-points/NetworkStatus.ts';
import { useConsumptionEvents } from '@confluence/unified-analytics/entry-points/useConsumptionEvents.ts';
import { Tree } from '@confluence/tree/entry-points/Tree.ts';
import type {
	ItemId,
	TreeItem,
	RenderItemParams,
} from '@confluence/tree/entry-points/tree-types.ts';
import { CONTEXT_PATH } from '@confluence/named-routes/entry-points/context.ts';
import { getURLBypassingResumeDraftAction } from '@confluence/content-utils/entry-points/resumeDraftActionBypass.ts';
import { usePageContentId } from '@confluence/page-context';
import { scrollElementToViewCenter } from '@confluence/dom-helpers';
import { PageSegmentLoadEnd } from '@confluence/browser-metrics/entry-points/PageSegmentLoadEnd.ts';
import { focusedAriaCurrentValue } from '@confluence/page-tree/entry-points/focusedAriaCurrentValue.ts';
import {
	BLOG_TREE_EXPERIENCE,
	BLOG_TREE_PAGINATION_EXPERIENCE,
} from '@confluence/experience-tracker/entry-points/ExperienceName.ts';
import {
	ExperienceSuccess,
	ExperienceTrackerContext,
} from '@confluence/experience-tracker/entry-points/component.ts';
import { ContentIcon } from '@confluence/icons/entry-points/ContentIcon.ts';
import { fg } from '@confluence/feature-gating';
import { useOptimisticTitleChange } from '@confluence/page-tree-refresh-state-container/entry-points/useOptimisticTitleChange.ts';
import { BLOG_TREE_SEGMENT_KEY } from '@confluence/browser-metrics/entry-points/metric-keys.ts';

import { INDENT_PER_LEVEL, VIRTUAL_ROOT_ID } from './treeParameters.js';
import type { BlogTreeItem } from './BlogTreeRootComponent.tsx';

const i18n = defineMessages({
	draftLozengeText: {
		id: 'blog-tree.status.draft',
		defaultMessage: 'DRAFT',
		description: 'User has started a blog but has not published it yet',
	},
	draftLozengeTextUpdated: {
		id: 'blog-tree.status.draft.updated',
		defaultMessage: 'Draft',
		description: 'User has started a blog but has not published it yet',
	},
	untitledDraftText: {
		id: 'blog-tree.untitled-draft',
		defaultMessage: 'Untitled',
		description: 'A placeholder for drafts that have no title',
	},
	loadMoreButton: {
		id: 'blog-tree.load-more-button',
		defaultMessage: 'Show more blogs',
		description: 'button to load more blogs in the blog tree',
	},
});

const stylizedLoadingSpinner = xcss({
	display: 'flex',
	flexShrink: 1,
	flexGrow: 1,
	flexBasis: '100%',
	justifyContent: 'center',
});

const titleStyles = xcss({
	overflow: 'hidden',
	whiteSpace: 'nowrap',
	textOverflow: 'ellipsis',
	opacity: 1,
});

const draftTitleStyles = xcss({
	opacity: '0.7', // Drafts should be 70% opacity
});

const loadMoreButtonStyles = xcss({
	marginTop: 'space.100',
	marginBottom: 'space.100',
});

const blogpostIndentationStyle = css({
	// eslint-disable-next-line @atlaskit/ui-styling-standard/no-nested-selectors
	'li li': {
		paddingLeft: token('space.300'),
		marginLeft: token('space.025'),
	},
});

type BlogTreeComponentProps = {
	spaceKey: string;
	blogTreeData: Record<ItemId, BlogTreeItem>;
	shouldShowLoadMoreButton: boolean;
	networkStatus: NetworkStatus;
	fetchMoreBlogs: () => void;
	loading: boolean;
	pageTreeFinishedLoading: boolean;
};

type LoadMoreButtonProps = {
	networkStatus: NetworkStatus;
	fetchMoreBlogs: () => void;
	loading: boolean;
	createAnalyticsEvent: CreateUIAnalyticsEvent;
};

const getItemTitle = (item: BlogTreeItem, isDraft: boolean): string | JSX.Element => {
	const untitledPlaceholder = isDraft ? <FormattedMessage {...i18n.untitledDraftText} /> : '';
	return item?.data?.title || untitledPlaceholder;
};

const BlogYearParentTreeLinkItem = ({
	year,
	item,
	onExpand = () => {},
	onCollapse = () => {},
	createAnalyticsEvent,
}: {
	year: string | JSX.Element;
	item: TreeItem;
	onCollapse?: (item: ItemId) => void;
	onExpand?: (item: ItemId) => void;
	createAnalyticsEvent: CreateUIAnalyticsEvent;
}) => {
	const { isExpanded } = item;

	const toggleCollapse = isExpanded ? onCollapse : onExpand;

	const handleClickBlogYear = () => {
		toggleCollapse(item.id);
		createAnalyticsEvent({
			type: 'sendUIEvent',
			data: {
				action: isExpanded ? 'collapsed' : 'expanded',
				actionSubject: 'blogTree',
				actionSubjectId: 'blogYear',
				source: 'sideNavigation',
				attributes: {
					year,
				},
			},
		}).fire();
	};

	return (
		<ExpandableMenuItem isDefaultExpanded>
			<ExpandableMenuItemTrigger onClick={handleClickBlogYear}>
				<Box xcss={titleStyles}>{year}</Box>
			</ExpandableMenuItemTrigger>
		</ExpandableMenuItem>
	);
};

const BlogTreeLinkItem = ({
	spaceKey,
	title,
	item,
	isDraft,
	isSelected,
	loading,
	createAnalyticsEvent,
	pageTreeFinishedLoading,
	isBlogTreeInitialLoadRef,
}: {
	spaceKey: string;
	title: string | JSX.Element;
	item: BlogTreeItem;
	isDraft: boolean;
	isSelected?: boolean;
	loading?: boolean;
	createAnalyticsEvent: CreateUIAnalyticsEvent;
	pageTreeFinishedLoading: boolean;
	isBlogTreeInitialLoadRef?: React.MutableRefObject<boolean>;
}) => {
	const originalURL = `${CONTEXT_PATH}${item?.data?.webui}`;
	const blogTreeItemRef = useRef(null);
	const { sendInitiatedEvent } = useConsumptionEvents();

	const stringTitle = typeof title === 'string' ? title : title.toString();
	const { optimisticTitle } = useOptimisticTitleChange(item.id, stringTitle);
	const displayTitle = typeof title === 'string' ? optimisticTitle : title;

	const bypassURL = getURLBypassingResumeDraftAction({
		url: originalURL,
		spaceKey,
		contentType: 'blogpost',
		editorVersion: item?.data?.editorVersion || '',
		contentId: item?.id,
	});

	useEffect(() => {
		// Essentially, we should only scroll to the selected blog tree item when:
		// Blog tree has finished loading ("!loading"), page tree has finished loading, AND this is the initial load of blog tree (isBlogTreeInitialLoadRef) and not a rerender from clicking load more
		if (
			isSelected &&
			!loading &&
			blogTreeItemRef.current &&
			isBlogTreeInitialLoadRef?.current &&
			pageTreeFinishedLoading
		) {
			scrollElementToViewCenter(blogTreeItemRef.current);

			isBlogTreeInitialLoadRef.current = false;
		}
	}, [loading, isSelected, pageTreeFinishedLoading, isBlogTreeInitialLoadRef]);

	const handleClickBlogTreeItem = () => {
		createAnalyticsEvent({
			type: 'sendUIEvent',
			data: {
				action: 'clicked',
				actionSubject: 'blogTree',
				actionSubjectId: 'blogpost',
				source: 'sideNavigation',
				attributes: {
					createdYear: item?.data?.createdYear,
					isDraft,
				},
			},
		}).fire();

		sendInitiatedEvent({
			contentId: item.id,
			spaceKey,
			source: 'BlogTree',
			utmSource: 'BLOGTREE',
		});
	};

	return (
		<LinkMenuItem
			testId="blog-tree-item"
			href={bypassURL}
			ref={isSelected ? blogTreeItemRef : null}
			onClick={handleClickBlogTreeItem}
			isSelected={isSelected}
			aria-current={isSelected ? focusedAriaCurrentValue : undefined} // this is required for SSR scrolling to selected blog tree item
			elemBefore={<ContentIcon type="blogpost" emoji={item?.data?.emoji} />}
		>
			<Grid columnGap="space.100" templateColumns={`${isDraft ? 'auto ' : ''}1fr`}>
				<Box xcss={[titleStyles, isDraft && draftTitleStyles]}>{displayTitle}</Box>
				{isDraft && (
					<span>
						<Lozenge appearance="default">
							<FormattedMessage
								{...(fg('confluence_lozenge_text_update')
									? i18n.draftLozengeTextUpdated
									: i18n.draftLozengeText)}
							/>
						</Lozenge>
					</span>
				)}
			</Grid>
		</LinkMenuItem>
	);
};

const LoadingSpinner = () => (
	<Box xcss={stylizedLoadingSpinner}>
		<AKSpinner size={20} />
	</Box>
);

const LoadMoreButton: FC<LoadMoreButtonProps> = ({
	loading,
	networkStatus,
	fetchMoreBlogs,
	createAnalyticsEvent,
}) => {
	const experienceTracker = useContext(ExperienceTrackerContext);

	if (loading && networkStatus === NetworkStatus.fetchMore) {
		return <LoadingSpinner />;
	}

	const handleLoadMoreBlogsClick = () => {
		experienceTracker.start({
			name: BLOG_TREE_PAGINATION_EXPERIENCE,
		});
		createAnalyticsEvent({
			type: 'sendUIEvent',
			data: {
				action: 'clicked',
				actionSubject: 'blogTree',
				actionSubjectId: 'loadMoreButton',
				source: 'sideNavigation',
			},
		}).fire();
		fetchMoreBlogs();
	};

	return (
		<Box xcss={loadMoreButtonStyles}>
			<Button shouldFitContainer spacing="compact" onClick={handleLoadMoreBlogsClick}>
				<FormattedMessage {...i18n.loadMoreButton} />
			</Button>
		</Box>
	);
};

export const BlogTreeComponent: FC<BlogTreeComponentProps> = ({
	spaceKey,
	blogTreeData,
	shouldShowLoadMoreButton,
	networkStatus,
	fetchMoreBlogs,
	loading,
	pageTreeFinishedLoading,
}) => {
	const [contentIdUserIsViewing] = usePageContentId();
	const [collapsedYears, setCollapsedYears] = useState<ItemId[]>([]);
	const { createAnalyticsEvent } = useAnalyticsEvents();
	const isBlogTreeInitialLoadRef = useRef<boolean>(true);

	const blogTreeDataWithCollapsedYears = Object.keys(blogTreeData).reduce(
		(acc, key) => {
			acc[key] = {
				...blogTreeData[key],
				isExpanded: !collapsedYears.includes(key),
			};
			return acc;
		},
		{} as Record<string, BlogTreeItem>,
	);

	const onCollapseYear = useCallback(
		(itemId: ItemId) => {
			if (!collapsedYears.includes(itemId)) {
				setCollapsedYears([...collapsedYears, itemId]);
			}
		},
		[collapsedYears],
	);

	const onExpandYear = useCallback(
		(itemId: ItemId) => {
			if (collapsedYears.includes(itemId)) {
				setCollapsedYears(collapsedYears.filter((id) => itemId !== id));
			}
		},
		[collapsedYears],
	);

	const renderItem = ({ item, onExpand, onCollapse }: RenderItemParams<BlogTreeItem>) => {
		const isDraft = item?.data?.status === 'draft';
		const isSelected = item?.id === contentIdUserIsViewing; //item is selected if the content id matches the blog's id
		const title = getItemTitle(item, isDraft);

		return (
			<Tooltip content={title} position="top" delay={750} hideTooltipOnMouseDown>
				{item.data && item.hasChildren ? ( // only years are parent items for blog tree
					<BlogYearParentTreeLinkItem
						year={title}
						item={item}
						onExpand={onExpand}
						onCollapse={onCollapse}
						createAnalyticsEvent={createAnalyticsEvent}
					/>
				) : (
					<BlogTreeLinkItem
						title={title}
						item={item}
						isDraft={isDraft}
						spaceKey={spaceKey}
						isSelected={isSelected}
						loading={loading}
						createAnalyticsEvent={createAnalyticsEvent}
						pageTreeFinishedLoading={pageTreeFinishedLoading}
						isBlogTreeInitialLoadRef={isBlogTreeInitialLoadRef}
					/>
				)}
			</Tooltip>
		);
	};
	const count = useMemo(
		() => Object.keys(blogTreeData).filter((key) => !blogTreeData[key].hasChildren).length,
		[blogTreeData],
	);

	return loading && networkStatus === NetworkStatus.loading ? (
		<LoadingSpinner />
	) : (
		<div css={blogpostIndentationStyle}>
			<Tree<BlogTreeItem>
				tree={{
					rootId: VIRTUAL_ROOT_ID,
					items: blogTreeDataWithCollapsedYears,
				}}
				isDragEnabled={false}
				renderItem={renderItem}
				onCollapse={onCollapseYear}
				onExpand={onExpandYear}
				indentPerLevel={INDENT_PER_LEVEL}
			/>
			{shouldShowLoadMoreButton && (
				<LoadMoreButton
					networkStatus={networkStatus}
					loading={loading}
					fetchMoreBlogs={fetchMoreBlogs}
					createAnalyticsEvent={createAnalyticsEvent}
				/>
			)}
			<PageSegmentLoadEnd metricKey={BLOG_TREE_SEGMENT_KEY} />
			<ExperienceSuccess name={BLOG_TREE_EXPERIENCE} attributes={{ count }} />
		</div>
	);
};
